import React, { useEffect, useRef, useState } from "react";
import { cn } from "../lib/api";
import { Button } from "./ui";

const FLIP_MS = 1600;

// Fair 50/50 from the browser's secure random source
const coinSide = () => {
  const a = new Uint8Array(1);
  window.crypto.getRandomValues(a);
  return a[0] & 1;
};

/**
 * Toss a coin between the two teams. The coin flies up, spins and lands on the winner;
 * the winner can then pick bat or bowl, which calls onChoice(battingTeamIndex).
 */
export default function CoinToss({ teams, battingFirst, onChoice }) {
  const [phase, setPhase] = useState("idle"); // idle | flipping | done
  const [winner, setWinner] = useState(null);
  const [choice, setChoice] = useState(null); // "bat" | "bowl" — what the toss winner picked
  const [flipId, setFlipId] = useState(0); // restarts the animation on a re-toss
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const name = (i) => teams[i]?.name?.trim() || `Team ${i + 1}`;

  const toss = () => {
    const side = coinSide();
    setWinner(side);
    setChoice(null);
    setPhase("flipping");
    setFlipId((n) => n + 1);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setPhase("done"), FLIP_MS);
  };

  const choose = (bat) => {
    setChoice(bat ? "bat" : "bowl");
    onChoice(bat ? winner : 1 - winner);
  };
  // Only highlight the pick while it still matches the batting order (it can be changed by hand below)
  const picked = choice && (choice === "bat") === (battingFirst === winner) ? choice : null;

  return (
    <div className="mb-4" data-testid="coin-toss">
      {phase === "idle" ? (
        <Button type="button" variant="outline" className="w-full" onClick={toss} data-testid="coin-toss-btn">
          <span aria-hidden className="text-lg">🪙</span> Toss coin
        </Button>
      ) : (
        <div className="rounded-2xl bg-black/[0.03] pt-4 pb-3 px-3 text-center">
          {/* Coin: face A = team 1, face B (rotated 180°) = team 2 */}
          <div className="relative h-48 flex items-end justify-center" style={{ perspective: 600 }}>
            <div
              key={flipId}
              className="coin-flip relative h-20 w-20 mb-3"
              style={{ transformStyle: "preserve-3d", "--coin-end": winner ? "180deg" : "0deg" }}
              aria-hidden
            >
              <CoinFace label={name(0)} letter="A" />
              <CoinFace label={name(1)} letter="B" back />
            </div>
            <div key={`s${flipId}`} className="coin-shadow absolute bottom-1 h-2 w-16 rounded-full bg-black" />
          </div>

          <div className="min-h-[28px]" aria-live="polite">
            {phase === "flipping" ? (
              <div className="text-sm text-ink-muted font-semibold">Tossing…</div>
            ) : (
              <div className="text-base font-bold" data-testid="coin-toss-result">
                <span className="text-brand">{name(winner)}</span> won the toss 🎉
              </div>
            )}
          </div>

          {phase === "done" && (
            <>
              <div className="text-xs text-ink-muted mt-1 mb-2">{name(winner)} chose to…</div>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" size="sm" variant={picked === "bat" ? "primary" : "outline"} className="h-10"
                  onClick={() => choose(true)} aria-pressed={picked === "bat"} data-testid="toss-bat">
                  🏏 Bat first
                </Button>
                <Button type="button" size="sm" variant={picked === "bowl" ? "primary" : "outline"} className="h-10"
                  onClick={() => choose(false)} aria-pressed={picked === "bowl"} data-testid="toss-bowl">
                  🎯 Bowl first
                </Button>
              </div>
              <button type="button" onClick={toss} className="text-xs font-semibold text-ink-muted underline mt-2.5" data-testid="coin-retoss">
                Toss again
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CoinFace({ label, letter, back }) {
  return (
    <div
      className={cn(
        "absolute inset-0 rounded-full grid place-items-center text-center px-2",
        "bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 shadow-[inset_0_0_0_4px_rgba(255,255,255,.35),inset_0_-6px_12px_rgba(0,0,0,.18)]",
      )}
      style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: back ? "rotateX(180deg)" : undefined }}
    >
      <div className="min-w-0">
        <div className="text-[10px] font-black text-amber-900/70 leading-none">{letter}</div>
        <div className="text-[11px] font-extrabold text-amber-950 leading-tight line-clamp-2 break-words">{label}</div>
      </div>
    </div>
  );
}
